import { useEffect, useRef } from 'react';
import type { FlightExplorerOffer } from './search_source.js';
import type { ExplorerState } from './explorer_state.js';
import { legSearchParams } from './explorer_request.js';
import type { FlightExplorerRequest, FlightSearchParams } from './search_source.js';

/** Params da busca de mount de uma perna: os da perna + a âncora, quando há
 *  (qualquer ref da mesma busca serve, ver `FlightSearchParams.anchorOfferId`). */
function anchoredParams(
  request: FlightExplorerRequest,
  leg: number,
  anchor: string | undefined,
): FlightSearchParams {
  return { ...legSearchParams(request, leg), ...(anchor ? { anchorOfferId: anchor } : {}) };
}

/** A âncora de uma perna SEMEADA é a primeira oferta da semente. */
const seedAnchor = (seed: FlightExplorerOffer[]): string | undefined => seed[0]?.offerId;

/** O que a busca do mount usa do IO do explorer (`useExplorerIo`) — estrutural,
 *  pra este módulo não importar o componente que o importa. */
interface InitialSearchIo {
  runSearch: (params: FlightSearchParams) => void;
  runLegSearch: (leg: number, params: FlightSearchParams) => void;
  refreshLeg: (leg: number, params: FlightSearchParams) => void;
}

/**
 * A busca de MONTAGEM do explorer: uma por perna `idle` (fan-out no multi) e a
 * re-busca viva por cima de cada perna SEMEADA. Extraída do `explorer.tsx` pelo
 * teto de linhas do arquivo.
 *
 * `reuseAnchorOfferId` é a âncora do HOST sem semente (o card de ida e volta,
 * #5793): só a perna `idle` do one-way/RT a usa, e só aqui — as buscas que
 * re-formam o pedido montam os params do zero.
 */
export function useInitialSearch(
  readOnly: boolean | undefined,
  state: ExplorerState,
  io: InitialSearchIo,
  reuseAnchorOfferId?: string,
): void {
  // O StrictMode (dev) desmonta e remonta o efeito de propósito, e cada rodada
  // saía com um `POST /flight/search` — 45–90 s de fornecedor por busca, em
  // dobro a cada montagem local (#3239 C7). A ref sobrevive à remontagem
  // simulada e segura a segunda rodada; abortar no cleanup não serviria, o
  // pedido já teria saído e o BFF já estaria buscando. O reducer descartaria a
  // segunda resposta pelo `seq` de qualquer jeito — o que se poupa é o pedido.
  // A ref também faz da busca uma só a do MOUNT: as deps re-entram no efeito, mas só
  // a primeira rodada passa — um `readOnly` que cai depois não dispara busca nenhuma.
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    if (readOnly) return;
    if (state.request.mode === 'multi_leg') {
      state.legs.forEach((leg, i) => {
        if (leg.phase === 'idle') io.runLegSearch(i, legSearchParams(state.request, i));
        else if (leg.phase === 'seeded') {
          io.refreshLeg(i, anchoredParams(state.request, i, seedAnchor(leg.offers)));
        }
      });
      return;
    }
    const first = state.legs[0];
    if (first?.phase === 'idle') io.runSearch(anchoredParams(state.request, 0, reuseAnchorOfferId));
    else if (first?.phase === 'seeded') {
      io.refreshLeg(0, anchoredParams(state.request, 0, seedAnchor(first.offers)));
    }
  }, [readOnly, state.request, state.legs, io, reuseAnchorOfferId]);
}
